import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { clearCart } from '../redux/slices/cartSlice';
import { BackButton } from '../components';
import { useCheckoutForm, OrderType } from '../hooks/useCheckoutForm';
import { useOrderCalculations } from '../hooks/useOrderCalculations';
import { useOrderManagement } from '../hooks/useOrderManagement';
import {
  CheckoutForm,
  OrderSummary,
  EmptyCart,
  TakeoutSuccessPopup,
  PaymentSuccessPopup,
  PaymentFailedPopup,
  PaymentFailedProcessingPopup,
} from '../components/checkout';

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { items: cartItems } = useAppSelector(state => state.cart);
  const dispatch = useAppDispatch();
  
  // Custom hooks
  const {
    formData,
    errors,
    handleChange,
    validateForm,
  } = useCheckoutForm();
  
  const { subtotal, deliveryFee, tax, total } = useOrderCalculations(cartItems, 'takeout' as OrderType);
  
  const {
    submittingMethod,
    showTakeoutSuccessPopup,
    showPaymentFailedPopup,
    showPaymentFailedProcessingPopup,
    showPaymentSuccessPopup,
    orderLoading,
    orderResponse,
    handleTakeoutOrder,
    handlePayNowOrder,
    handleContinueShopping,
    handlePaymentSuccess,
    handlePaymentRetry,
    resetAllPopupStates,
    initiatePayment,
  } = useOrderManagement();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      handlePayNowOrder(formData, total, 'takeout' as OrderType);
    }
  }, [validateForm, handlePayNowOrder, formData, total]);

  const handleTakeoutSubmit = useCallback(() => {
    if (validateForm()) {
      handleTakeoutOrder(formData, total, 'takeout' as OrderType);
    }
  }, [validateForm, handleTakeoutOrder, formData, total]);

  // Show empty cart if no items
  if (cartItems.length === 0) {
    return <EmptyCart />;
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      {/* Checkout Content */}
      <div className="container mx-auto py-12 px-4">
        <div className="mb-8">
          <BackButton text="Back to Menu" onClick={() => navigate('/menu')} />
          <h1 className="text-4xl font-bold">Checkout</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <CheckoutForm
              formData={formData}
              errors={errors}
              handleChange={handleChange}
              onSubmit={handleFormSubmit}
              onTakeoutOrder={handleTakeoutSubmit}
              orderLoading={orderLoading}
              submittingMethod={submittingMethod}
              cartItemsCount={cartItems.length}
            />
          </div>

          {/* Order Summary */}
          <div>
            <OrderSummary
              cartItems={cartItems}
              orderType={'takeout' as OrderType}
              subtotal={subtotal}
              deliveryFee={deliveryFee}
              tax={tax}
              total={total}
            />
          </div>
        </div>
      </div>

      {/* Takeout Success Popup */}
      <TakeoutSuccessPopup
        isOpen={showTakeoutSuccessPopup}
        onContinue={handleContinueShopping}
      />

      {/* Payment Popups */}
      <PaymentSuccessPopup
        isOpen={showPaymentSuccessPopup}
        onClose={() => resetAllPopupStates()}
        onContinue={() => handlePaymentSuccess(() => {
          dispatch(clearCart());
          // Navigate to menu after successful payment
          navigate('/menu');
        })}
      />

      <PaymentFailedPopup
        isOpen={showPaymentFailedPopup}
        onClose={() => resetAllPopupStates()}
        onTryAgain={() => handlePaymentRetry(() => {
          // Retry the payment with the same payment link
          if (orderResponse?.payment_link) {
            initiatePayment(orderResponse.payment_link);
          }
        })}
      />

      <PaymentFailedProcessingPopup
        isOpen={showPaymentFailedProcessingPopup}
        onClose={() => resetAllPopupStates()}
        onTryAgain={() => handlePaymentRetry(() => {
          // Retry the payment with the same payment link
          if (orderResponse?.payment_link) {
            initiatePayment(orderResponse.payment_link);
          }
        })}
      />
    </div>
  );
};

export default CheckoutPage;
